import { ApolloError, ReactiveVar } from '@apollo/client';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { kanbanJobTitleId, notificationsVar, occupationPreferenceId } from '../../cache/cache';
import {
  emptyJobPreferencesCache,
  IJobPreferencesCache,
  IJobPreferencesVar,
  IJobTitleSkillSetVar,
  jobPreferencesVar,
} from '../../cache/learner/jobPreferencesReactiveVar';
import { IKanbanCache, IKanbanVar, ISkillKanban, StudentSkillStatus } from '../../cache/learner/kanbanReactiveVar';
import { RecommendedOccupationsByMajorReactiveVar } from '../../cache/learner/recommendedOccupationsByMajorReactiveVar';
import { RecommendedOccupationsReactiveVar } from '../../cache/learner/recommendedOccupationsReactiveVar';
import { RelatedOccupationsReactiveVar } from '../../cache/learner/relatedOccupationsReactiveVar';
import { OccupationComparisonType } from '../../components/generics/OccupationComparison/OccupationComparisonConfig';
import { SkillStatusEnum } from '../../constants/enums/SkillStatusEnum';
import { Mixpanel } from '../../mixpanel/mixpanel';
import { MixpanelEventsEnum } from '../../mixpanel/MixpanelEvents/mixpanelEventsInterface';
import {
  IJobPreferencesQueryHook,
  IJobTitleSkillSet,
} from '../../operations/query/students/useGetLearnerEntireProfile/useGetLearnerEntireProfile';
import {
  getJobRelatedOccupations,
  IRelatedOccupations,
  useLazyGetJobRelatedOccupations,
} from '../../operations/query/students/useGetRelatedOccupations';
import { decryptor, encryptor } from '../../utils/decryptor';
import { getMixpanelName } from '../../utils/getMixpanelName';
import { ROLE_ENUM } from '../../views/Auth/Login/interfaces/IGoogleUser';
import StudentSkilltrackerFiltersEnum from '../../views/Learner/Skills/components/KanbanBoard/interfaces/StudentSkilltrackerFiltersEnum';
import RecommendedOccupationsByMajorHook from '../recommendedOccupationsByMajorHook/recommendedOccupationsByMajorHook';
import RecommendedOccupationHook from '../RecommendedOccupationsHook/recommendedOccupationsHook';
import useLogger from '../useLogger/useLogger';
import userAuth from '../userAuth';
import useStudentRefetch from '../useStudentRefetch';

export interface IROIResult {
  currentMatch: number;
  improvement: number;
  targetMatch: number;
  skills: string[];
}

interface IUseJobPreferencesDataHook {
  operations: {
    setJobPreferences: (jobPreferences: IJobPreferencesQueryHook[]) => void;
    setJobPreferencesOrganized: (data: IJobPreferencesVar[]) => void;
    setJobPreferencesError: (errorMessage: ApolloError | undefined) => void;
    clearJobPreferencesError: () => void;
    getSkillSetByJobTitle: (jobTitleId: string) => IJobTitleSkillSetVar[];
    isSkillInJobPreferences: (skillId: string) => boolean;
    getOccupationChanges: (skillId: string) => string;
    filterJobPreferencesById: (filterId: string) => IJobPreferencesVar | undefined;
    setJobPreferencesRelatedOccupations: (relatedOccupations: IRelatedOccupations[]) => void;
    setFromLocalJobPreferences: () => boolean;
    addSkillAndRecalculateMatch: (skillId: string, added?: boolean) => void;
    addJobPreference: (
      jobPreference: IJobPreferencesVar,
      mixPanelData: { component: string; replaced: boolean; recommendedBy: string },
    ) => void;
    deleteJobPreference: (jobTitleId: string, component: string) => void;
    filterJobPreferencesByCurrentId: (currentJobTitleId: string) => IJobPreferencesVar | undefined;
    calculateJobPreferenceMatch: (jobPreferenceId: string) => number;
    calculateMatchImprovement: (jobPreferenceId: string, learningPathSkillIds: string[]) => number;
    calculateMatchImprovementForAllOccupations: (learningPathSkillIds: string[]) => Map<string, IROIResult>;
    replaceJobPreference: (
      replaceId: string,
      jobPreferenceToAdd: IJobPreferencesVar,
      mixPanelData: { component: string; replaced: boolean; recommendedBy: string },
    ) => Promise<void>;
    occupationsThatContainSkill: ({ skillName }: { skillName: string }) => string;
    getFirstOccupationThatContainsSkill: ({ skillName }: { skillName: string }) => string;
    getNumberOfOccupationsAffected: (skillName: string) => number;
    updateSkillProficiencyLevel: (skillId: string, proficiencyLevel: number | undefined) => void;
    calculateOccupationComparison: (params: {
      compareType: OccupationComparisonType;
      occupationIds: string[];
    }) => IOccupationComparisonResult;
  };
}

export interface IOccupationComparisonDatum {
  name: string;
  value: number | undefined;
  jobTitleId: string;
}

export interface IOccupationComparisonResult {
  data: IOccupationComparisonDatum[];
  loading: boolean;
}

enum SkillTypeEnum {
  SPECIALIZED = 'specialized',
  HUMAN = 'human',
}

interface ISkillsCounter {
  specializedAcquired: number;
  specializedTotal: number;
  humanAcquired: number;
  humanTotal: number;
}

const useJobPreferencesDataHook = (
  jobPreferencesVarProp: ReactiveVar<IJobPreferencesCache>,
  kanbanVarProp: ReactiveVar<IKanbanCache>,
): IUseJobPreferencesDataHook => {
  const jobPreferencesData = jobPreferencesVarProp();
  const kanbanData = kanbanVarProp();
  const { auth } = userAuth();
  const { pathname, search } = useLocation();
  const { operations } = useLogger();
  const isLearner: boolean = auth?.userRole === ROLE_ENUM.STUDENT;
  const { t } = useTranslation('useJobPreferencesDataHookConfig');
  const { updateRecommendedOccupations } = RecommendedOccupationHook(RecommendedOccupationsReactiveVar);
  const { updateRecommendedOccupationsByMajor } = RecommendedOccupationsByMajorHook(
    RecommendedOccupationsByMajorReactiveVar,
  );
  const { refetchPathways } = useStudentRefetch();

  const [getRelatedOccupations, { data: relatedOccupationsData, error: relatedOccupationsError }] =
    useLazyGetJobRelatedOccupations(false);

  const getOccupationChanges = (skillId: string): string => {
    const occupationsAffected = jobPreferencesData.data.reduce(
      (count, jobPreference) =>
        count + (jobPreference.jobTitleSkillSet.some((skill) => skill.skillId === skillId) ? 1 : 0),
      0,
    );

    return occupationsAffected.toString();
  };

  const getSkillSetByJobTitle = (jobTitleId: string): IJobTitleSkillSetVar[] => {
    const matchingJobPreference = jobPreferencesData.data.find(
      (jobPreference) => jobPreference.jobTitleId === jobTitleId,
    );

    const skillSet = matchingJobPreference?.jobTitleSkillSet || [];

    return skillSet;
  };

  const isSkillInJobPreferences = (skillId: string): boolean =>
    jobPreferencesData.data.some((jobPreference) =>
      jobPreference.jobTitleSkillSet.some((skill) => skill.skillId === skillId),
    );

  const clearJobPreferencesError = (): void => {
    jobPreferencesVar(emptyJobPreferencesCache);
  };

  const setJobPreferencesError = (errorMessage: ApolloError | undefined): void => {
    const copyOfData = structuredClone(jobPreferencesData);

    copyOfData.error = errorMessage;
    jobPreferencesVar(copyOfData);
  };

  const setJobPreferences = (jobPreferences: IJobPreferencesQueryHook[]): void => {
    if (jobPreferences) {
      const data: IJobPreferencesVar[] = jobPreferences?.map((jobPreference: IJobPreferencesQueryHook) => ({
        jobTitleId: jobPreference.jobTitleId,
        jobTitleName: jobPreference.jobTitleName,
        jobTitleSkillSet:
          jobPreference.jobTitleSkillSet.map((skillSet: IJobTitleSkillSet) => ({
            skillId: skillSet.skill,
            skillName: skillSet.skillName,
            skillType: skillSet.skillType,
            skillTypeTranslated: skillSet.skillTypeTranslated,
            jobTitleId: skillSet.jobTitle,
            validatedSkill: skillSet.validatedSkill,
            originalName: skillSet.originalName,
            seniorityRequirements: skillSet.seniorityRequirements,
            skillProficiencyLevel: skillSet.skillProficiencyLevel,
          })) || [],
      }));

      const encryptedData = encryptor(JSON.stringify(data));

      jobPreferencesVar({ error: jobPreferencesData.error, data });

      if (!encryptedData) {
        return;
      }

      sessionStorage.setItem('jobPreferences', encryptedData);
    }
  };

  const setJobPreferencesRelatedOccupations = (relatedOccupations: IRelatedOccupations[]): void => {
    const updatedData = jobPreferencesData.data.map((jobPreference) => {
      const relatedOccupationData =
        relatedOccupations
          .find((relatedOccupation) => relatedOccupation.jobTitleId === jobPreference.jobTitleId)
          ?.data.slice()
          .sort((a, b) => b.match - a.match) ?? [];

      return {
        ...jobPreference,
        relatedOccupations: relatedOccupationData,
      };
    });

    jobPreferencesVar({ error: jobPreferencesData.error, data: updatedData });
  };

  const setJobPreferencesOrganized = (data: IJobPreferencesVar[]): void => {
    jobPreferencesVar({ error: jobPreferencesData.error, data });

    const encryptedData = encryptor(JSON.stringify(data));

    if (!encryptedData) {
      return;
    }

    sessionStorage.setItem('jobPreferences', encryptedData);
  };

  const addSkillAndRecalculateMatch = (skillId: string, added?: boolean): void => {
    const currentData = jobPreferencesVarProp();
    const copyOfData = structuredClone(currentData.data);
    let changed = false;

    copyOfData.forEach((jobPreference) => {
      const skillToChange = jobPreference.jobTitleSkillSet.find((skill) => skill.skillId === skillId);
      const jobPreferenceToChange = copyOfData.find(({ jobTitleId }) => jobTitleId === jobPreference.jobTitleId);
      const skillToModify = jobPreferenceToChange?.jobTitleSkillSet.find((skill) => skill.skillId === skillId);

      if (skillToChange && jobPreferenceToChange?.skillMatch && skillToModify && jobPreference.skillMatch) {
        if (skillToModify.skillType === 'human') {
          const previousAcquired = jobPreferenceToChange.skillMatch.humanAcquired;
          const newHumanAcquiredValue = added ? previousAcquired + 1 : previousAcquired - 1;
          const newHumanPercentage = calculatePercentage(newHumanAcquiredValue, jobPreference.skillMatch.humanTotal);

          const newMatch = calculatePercentage(
            newHumanAcquiredValue + jobPreference.skillMatch.specializedAcquired,
            jobPreference.skillMatch.specializedTotal + jobPreference.skillMatch.humanTotal,
          );

          jobPreferenceToChange.skillMatch.humanAcquired = newHumanAcquiredValue;
          jobPreferenceToChange.skillMatch.humanPercentage = newHumanPercentage;
          jobPreferenceToChange.skillMatch.match = newMatch;

          jobPreferenceToChange.match = newMatch;
        } else if (skillToModify.skillType === 'specialized') {
          const previousSpecialized = jobPreferenceToChange.skillMatch.specializedAcquired;
          const newSpecializedAcquiredValue = added ? previousSpecialized + 1 : previousSpecialized - 1;
          const newSpecializedPercentage = calculatePercentage(
            newSpecializedAcquiredValue,
            jobPreference.skillMatch.specializedTotal,
          );

          const newMatch = calculatePercentage(
            newSpecializedAcquiredValue + jobPreference.skillMatch.humanAcquired,
            jobPreference.skillMatch.specializedTotal + jobPreference.skillMatch.humanTotal,
          );

          jobPreferenceToChange.skillMatch.specializedAcquired = newSpecializedAcquiredValue;
          jobPreferenceToChange.skillMatch.specializedPercentage = newSpecializedPercentage;
          jobPreferenceToChange.match = newMatch;
          jobPreferenceToChange.skillMatch.match = newMatch;
        }

        changed = true;
      }
    });

    if (changed) {
      setJobPreferencesOrganized(copyOfData);
    }
  };

  const countSkills = (currentJobSkills: IJobTitleSkillSetVar[], kanbanSkills: ISkillKanban[]): ISkillsCounter => {
    const counter: ISkillsCounter = {
      specializedAcquired: 0,
      specializedTotal: 0,
      humanAcquired: 0,
      humanTotal: 0,
    };

    const specializedSkills = currentJobSkills.filter((jobSkill) => jobSkill.skillType === SkillTypeEnum.SPECIALIZED);
    const humanSkills = currentJobSkills.filter((jobSkill) => jobSkill.skillType !== SkillTypeEnum.SPECIALIZED);

    const specializedKanban = new Set(
      kanbanSkills
        .filter((kanbanSkill) => kanbanSkill.type === SkillTypeEnum.SPECIALIZED)
        .map((kanbanSkill) => kanbanSkill.skillId),
    );

    const humanKanban = new Set(
      kanbanSkills
        .filter((kanbanSkill) => kanbanSkill.type !== SkillTypeEnum.SPECIALIZED)
        .map((kanbanSkill) => kanbanSkill.skillId),
    );

    counter.specializedAcquired = specializedSkills.reduce(
      (accumulator, { skillId }) => accumulator + (specializedKanban.has(skillId) ? 1 : 0),
      0,
    );
    counter.specializedTotal = specializedSkills.length;
    counter.humanTotal = humanSkills.length;
    counter.humanAcquired = humanSkills.reduce(
      (accumulator, { skillId }) => accumulator + (humanKanban.has(skillId) ? 1 : 0),
      0,
    );

    return counter;
  };

  const calculatePercentage = (currentSkills: number, allSkills = 0): number =>
    allSkills < 1 ? 0 : Math.round((currentSkills * 100) / allSkills);

  const filterJobPreferencesByCurrentId = (currentJobTitleId: string): IJobPreferencesVar | undefined => {
    const currentPreference = jobPreferencesData.data.find(
      (jobPreference) => jobPreference.jobTitleId === currentJobTitleId,
    );

    if (!currentPreference) {
      return undefined;
    }

    const kanbanSkillsById = new Map(
      kanbanData.data
        .flatMap((column) => column.items)
        .map((kanbanSkill) => [kanbanSkill.skillId, kanbanSkill] as const),
    );

    const studentSkills = kanbanData.data.find((column) => column.id === StudentSkillStatus.COMPLETED)?.items || [];
    const skillCounter = countSkills(currentPreference?.jobTitleSkillSet, studentSkills);

    const specializedAcquiredPercentage = calculatePercentage(
      skillCounter.specializedAcquired + skillCounter.humanAcquired,
      currentPreference.jobTitleSkillSet.length,
    );

    const specializedPercentage = calculatePercentage(skillCounter.specializedAcquired, skillCounter.specializedTotal);

    const humanPercentage = calculatePercentage(skillCounter.humanAcquired, skillCounter.humanTotal);

    const jobTitleSkillSet = currentPreference.jobTitleSkillSet.map((skillSet) => {
      const kanbanSkill = kanbanSkillsById.get(skillSet.skillId);

      return {
        ...skillSet,
        skillName: kanbanSkill?.name ?? skillSet.skillName,
        originalName: kanbanSkill?.originalName ?? skillSet.originalName,
        skillKanbanStatus: kanbanSkill?.status ?? skillSet.skillKanbanStatus,
        studentAcquired: studentSkills.some((studentSkill) => studentSkill.skillId === skillSet.skillId),
        validatedSkill: studentSkills.some(
          (studentSkill) => studentSkill.skillId === skillSet.skillId && studentSkill.validatedSkill,
        ),
      };
    });

    const skillMatch = {
      jobTitleId: currentPreference?.jobTitleId,
      jobTitleName: currentPreference?.jobTitleName,
      match: specializedAcquiredPercentage,
      specializedPercentage,
      humanPercentage,
      specializedAcquired: skillCounter.specializedAcquired,
      specializedTotal: skillCounter.specializedTotal,
      humanAcquired: skillCounter.humanAcquired,
      humanTotal: skillCounter.humanTotal,
    };

    return {
      jobTitleId: currentPreference?.jobTitleId,
      jobTitleName: currentPreference?.jobTitleName,
      match: specializedAcquiredPercentage,
      jobTitleSkillSet,
      skillMatch,
    };
  };

  const filterJobPreferencesById = (filterId: string): IJobPreferencesVar | undefined =>
    jobPreferencesData.data.find((jobPreference) => jobPreference.jobTitleId === filterId);

  const setFromLocalJobPreferences = (): boolean => {
    const dataLocal = sessionStorage.getItem('jobPreferences');

    const decryptedData = decryptor(dataLocal ?? '');

    if (decryptedData) {
      jobPreferencesVar({ error: jobPreferencesData.error, data: JSON.parse(decryptedData) });

      return true;
    }

    return false;
  };

  const addJobPreference = (
    jobPreference: IJobPreferencesVar,
    mixPanelData: { component: string; replaced: boolean; recommendedBy: string },
  ): void => {
    const copyOfData = structuredClone(jobPreferencesData.data);

    copyOfData.push(jobPreference);
    setJobPreferencesOrganized(copyOfData);

    updateRecommendedOccupations(jobPreference.jobTitleId);
    updateRecommendedOccupationsByMajor(jobPreference.jobTitleId);
    const jobTitleIds = copyOfData.map(({ jobTitleId }) => jobTitleId) || [];

    occupationPreferenceId(jobTitleIds[0] || undefined);
    getRelatedOccupations({
      variables: {
        jobTitleIds,
      },
    });
    refetchPathways?.();

    Mixpanel.track(
      {
        action: MixpanelEventsEnum.ADD_OCCUPATION,
        data: {
          screen: `${getMixpanelName(pathname.concat(search))}`,
          component: mixPanelData.component,
          action: 'click',
          occupationName: jobPreference.jobTitleName,
          replaceAnOccupation: `${mixPanelData.replaced}`,
          recommendedBy: mixPanelData.recommendedBy,
        },
      },
      isLearner,
    );
  };

  const filterBacklogItems = (studentKanban: IKanbanVar, skillsToDelete: IJobTitleSkillSetVar[]): ISkillKanban[] => {
    if (studentKanban.id !== SkillStatusEnum.BACKLOG) {
      return studentKanban.items;
    }

    return studentKanban.items.filter((item) => !skillsToDelete.some((sd) => sd.skillId === item.skillId));
  };

  const deleteJobPreference = (jobTitleId: string, component: string): void => {
    const jobTitleSkills =
      structuredClone(jobPreferencesData.data).find((jt) => jt.jobTitleId === jobTitleId)?.jobTitleSkillSet ?? [];
    const otherJobTitlesSkills =
      structuredClone(jobPreferencesData.data)
        .filter((jt) => jt.jobTitleId !== jobTitleId)
        ?.flatMap(({ jobTitleSkillSet }) => jobTitleSkillSet) ?? [];
    const skillsToDelete = jobTitleSkills.filter(
      (skillJobTitle) =>
        !otherJobTitlesSkills.some((otherSkillJobTitle) => otherSkillJobTitle.skillId === skillJobTitle.skillId),
    );
    const studentSkills = structuredClone(kanbanData.data);
    const newBacklogSkills = studentSkills.map((studentKanban) => ({
      ...studentKanban,
      items: filterBacklogItems(studentKanban, skillsToDelete),
    }));

    kanbanVarProp({
      error: kanbanData.error,
      data: newBacklogSkills,
    });

    const copyOfData = structuredClone(jobPreferencesData.data);
    const indexToRemove = copyOfData.findIndex((jobPreference) => jobPreference.jobTitleId === jobTitleId);

    if (indexToRemove > -1) {
      const occupationName = copyOfData[indexToRemove].jobTitleName;

      copyOfData.splice(indexToRemove, 1);
      setJobPreferencesOrganized(copyOfData);
      const jobTitleIds = copyOfData.map((data) => data.jobTitleId);

      occupationPreferenceId(jobTitleIds[0] || undefined);

      getRelatedOccupations({
        variables: {
          jobTitleIds,
        },
      });

      refetchPathways?.();
      kanbanJobTitleId(StudentSkilltrackerFiltersEnum.ALL);

      Mixpanel.track(
        {
          action: MixpanelEventsEnum.DELETE_OCCUPATION,
          data: {
            screen: getMixpanelName(`${pathname}${search}`),
            component,
            action: 'click',
            occupationName,
          },
        },
        isLearner,
      );
    }
  };

  const replaceJobPreference = async (
    replaceId: string,
    jobPreferenceToAdd: IJobPreferencesVar,
    mixPanelData: { component: string; replaced: boolean; recommendedBy: string },
  ): Promise<void> => {
    const copyOfData = structuredClone(jobPreferencesData.data);
    const indexToRemove = copyOfData.findIndex((jobPreference) => jobPreference.jobTitleId === replaceId);

    if (indexToRemove > -1) {
      copyOfData.splice(indexToRemove, 1);
      copyOfData.push(jobPreferenceToAdd);
      setJobPreferencesOrganized(copyOfData);

      try {
        const { data, error } = await getJobRelatedOccupations(copyOfData.map(({ jobTitleId }) => jobTitleId));

        if (error) {
          notificationsVar([
            {
              id: error.name,
              title: error.name,
              description: error.message,
              autoClose: true,
              leftIcon: <WarningAmberRoundedIcon color="error" />,
            },
          ]);
        } else {
          if (data?.relatedOccupations) {
            RelatedOccupationsReactiveVar({ error: '', data: data.relatedOccupations });
          }

          occupationPreferenceId(copyOfData[0]?.jobTitleId);

          Mixpanel.track(
            {
              action: MixpanelEventsEnum.ADD_OCCUPATION,
              data: {
                screen: getMixpanelName(`${pathname}${search}`),
                component: mixPanelData.component,
                action: 'click',
                occupationName: jobPreferenceToAdd.jobTitleName,
                replaceAnOccupation: `${mixPanelData.replaced}`,
                recommendedBy: mixPanelData.recommendedBy,
              },
            },
            isLearner,
          );
        }
      } catch (error) {
        notificationsVar([
          {
            id: 'replaceJobPreference',
            title: t('ERROR_TITLE_REPLACE'),
            description: t('ERROR_DESCRIPTION_REPLACE'),
            autoClose: true,
            leftIcon: <WarningAmberRoundedIcon color="error" />,
          },
        ]);

        await operations.writeLogger({
          project: 'front-end',
          source: 'useJobPreferencesDataHook',
          func: 'replaceJobPreferences',
          error: error as string,
        });
      }
    }
  };

  const calculateMatchImprovement = useCallback(
    (jobPreferenceId: string, learningPathSkillIds: string[]): number => {
      if (!learningPathSkillIds.length) {
        return 0;
      }
      const occupation = jobPreferencesData.data.find((jobPreference) => jobPreference.jobTitleId === jobPreferenceId);

      if (!occupation?.jobTitleSkillSet?.length) {
        return 0;
      }

      const totalSkills = occupation.jobTitleSkillSet.length;
      const occupationSkillIds = new Set(occupation.jobTitleSkillSet.map((s) => s.skillId));

      const completedSkillIds = new Set(
        kanbanData.data
          .find((column) => column.id === StudentSkillStatus.COMPLETED)
          ?.items.map(({ skillId }) => skillId) ?? [],
      );

      const alreadyMatched = occupation.jobTitleSkillSet.reduce(
        (count, { skillId }) => count + (completedSkillIds.has(skillId) ? 1 : 0),
        0,
      );

      const additionalMatchedSkills = learningPathSkillIds.filter(
        (skillId) => occupationSkillIds.has(skillId) && !completedSkillIds.has(skillId),
      ).length;

      if (additionalMatchedSkills === 0) {
        return 0;
      }

      return (
        Math.round((100 * (alreadyMatched + additionalMatchedSkills)) / totalSkills) -
        Math.round((100 * alreadyMatched) / totalSkills)
      );
    },
    [jobPreferencesData.data, kanbanData.data],
  );

  const calculateMatchImprovementForAllOccupations = useCallback(
    (learningPathSkillIds: string[]): Map<string, IROIResult> => {
      const result = new Map<string, IROIResult>();

      jobPreferencesData.data.forEach((occupation) => {
        const currentMatch = Math.trunc(calculateJobPreferenceMatch(occupation.jobTitleId));
        const occupationSkillSet = occupation.jobTitleSkillSet;
        const improvement = Math.trunc(calculateMatchImprovement(occupation.jobTitleId, learningPathSkillIds));
        const targetMatch = Math.trunc(currentMatch + improvement);
        const completedSkillIds = new Set(
          kanbanData.data
            .find((column) => column.id === StudentSkillStatus.COMPLETED)
            ?.items.map(({ skillId }) => skillId) ?? [],
        );
        const skills = occupationSkillSet
          .filter((item) => !completedSkillIds.has(item.skillId))
          .map((item) => item.skillName)
          .slice(0, 3);

        result.set(occupation.jobTitleId, {
          currentMatch,
          improvement,
          targetMatch,
          skills,
        });
      });

      return result;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [jobPreferencesData.data, calculateMatchImprovement],
  );

  const calculateJobPreferenceMatch = useCallback(
    (jobPreferenceId: string): number => {
      const occupation = jobPreferencesData.data.find((jobPreference) => jobPreference.jobTitleId === jobPreferenceId);

      if (!occupation?.jobTitleSkillSet?.length) {
        return 0;
      }

      const studentSkills =
        kanbanData.data
          .find((column) => column.id === StudentSkillStatus.COMPLETED)
          ?.items.map(({ skillId }) => skillId) ?? [];

      const totalSkills = occupation.jobTitleSkillSet.length;

      const contains = occupation.jobTitleSkillSet.reduce(
        (value, currentSkill) => value + (studentSkills.includes(currentSkill.skillId) ? 1 : 0),
        0,
      );

      return Math.round((100 * contains) / totalSkills);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [jobPreferencesData.data, kanbanData.data],
  );

  useEffect(() => {
    if (relatedOccupationsError) {
      notificationsVar([
        {
          id: relatedOccupationsError.name,
          title: relatedOccupationsError.name,
          description: relatedOccupationsError.message,
          autoClose: true,
          leftIcon: <WarningAmberRoundedIcon color="error" />,
        },
      ]);
    } else {
      const relatedData = relatedOccupationsData?.relatedOccupations;

      if (relatedData) {
        RelatedOccupationsReactiveVar({ error: '', data: relatedData });
      }
    }
  }, [relatedOccupationsData, relatedOccupationsError]);

  const occupationsThatContainSkill = ({ skillName }: { skillName: string }): string => {
    const names: string[] = [];

    jobPreferencesData.data.forEach((jobPreference) => {
      jobPreference.jobTitleSkillSet.forEach((skillSet) => {
        if (skillSet.skillName === skillName) {
          names.push(jobPreference.jobTitleName);
        }
      });
    });

    return names.join(', ');
  };

  const getFirstOccupationThatContainsSkill = ({ skillName }: { skillName: string }): string => {
    const jobPreferenceWithSkill = jobPreferencesData.data.find((jobPreference) =>
      jobPreference.jobTitleSkillSet.some((skillSet) => skillSet.skillName === skillName),
    );

    return jobPreferenceWithSkill?.jobTitleName ?? jobPreferencesData.data[0]?.jobTitleName ?? '';
  };

  const getNumberOfOccupationsAffected = (skillName: string): number => {
    const occupationsAffected = jobPreferencesData.data.reduce(
      (count, jobPreference) =>
        count + (jobPreference.jobTitleSkillSet.some((skill) => skill.skillName === skillName) ? 1 : 0),
      0,
    );

    return occupationsAffected;
  };

  const calculateOccupationComparison = useCallback(
    ({
      compareType,
      occupationIds,
    }: {
      compareType: OccupationComparisonType;
      occupationIds: string[];
    }): IOccupationComparisonResult => {
      const data: IOccupationComparisonDatum[] = occupationIds.map((jobTitleId) => {
        const occupation = jobPreferencesData.data.find((jobPreference) => jobPreference.jobTitleId === jobTitleId);

        if (!occupation) {
          return { name: '', value: undefined, jobTitleId };
        }

        const value =
          compareType === OccupationComparisonType.SKILL_MATCH ? calculateJobPreferenceMatch(jobTitleId) : undefined;

        return {
          name: occupation.jobTitleName,
          value,
          jobTitleId,
        };
      });

      return {
        data,
        loading: false,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [jobPreferencesData.data, calculateJobPreferenceMatch],
  );

  const updateSkillProficiencyLevel = (skillId: string, proficiencyLevel: number | undefined): void => {
    const copyOfData = structuredClone(jobPreferencesData.data);

    // eslint-disable-next-line no-restricted-syntax
    for (const jobPreference of copyOfData) {
      const skillToUpdate = jobPreference.jobTitleSkillSet.find((skill) => skill.skillId === skillId);

      if (skillToUpdate) {
        skillToUpdate.skillProficiencyLevel = proficiencyLevel;
      }
    }

    setJobPreferencesOrganized(copyOfData);
  };

  return {
    operations: {
      setJobPreferences,
      setJobPreferencesOrganized,
      setJobPreferencesError,
      clearJobPreferencesError,
      getSkillSetByJobTitle,
      isSkillInJobPreferences,
      getOccupationChanges,
      filterJobPreferencesById,
      setJobPreferencesRelatedOccupations,
      setFromLocalJobPreferences,
      addJobPreference,
      deleteJobPreference,
      filterJobPreferencesByCurrentId,
      addSkillAndRecalculateMatch,
      calculateJobPreferenceMatch,
      calculateMatchImprovement,
      calculateMatchImprovementForAllOccupations,
      replaceJobPreference,
      occupationsThatContainSkill,
      getFirstOccupationThatContainsSkill,
      getNumberOfOccupationsAffected,
      updateSkillProficiencyLevel,
      calculateOccupationComparison,
    },
  };
};

export default useJobPreferencesDataHook;
